import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { Link } from '@tanstack/react-router'
import { useStore } from 'zustand'

import { ChartBarIcon } from '@gto/icons-react/outline/chart-bar'
import { ClockIcon } from '@gto/icons-react/outline/clock'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { UserGroupIcon } from '@gto/icons-react/outline/user-group'
import { Button } from '@gto/ui/button'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { useGetMyAnalytics, useGetMyCommissions, useGetMyStats } from '@/api/gen'
import { CommissionsTable } from '@/components/commissions-table'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { PerformanceChart } from '@/components/performance-chart'
import { StatsCard } from '@/components/stats-card'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { store } from '@/store'
import { isGranularity, useGranularitySnapshot } from '@/utils/granularity'
import { toAmount } from '@/utils/to-amount'
import { toNullablePct } from '@/utils/to-nullable-pct'

export const DashboardPage = () => {
  const { formatMessage } = useIntl()
  const partner = useStore(store, (s) => s.partner)
  // Snapshot the date window so `getDateRange()`'s `new Date()` runs on toggle,
  // not every render — keeps the TanStack Query key identity stable across
  // unrelated rerenders. Mirrors the admin dashboard chokepoint.
  const { granularity, dateRange, setGranularity } = useGranularitySnapshot('monthly')

  const statsQuery = useGetMyStats()
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery
  const analyticsQuery = useGetMyAnalytics({ ...dateRange, granularity })
  const { data: analytics, isError: analyticsError, refetch: refetchAnalytics } = analyticsQuery

  const commissionsQuery = useGetMyCommissions({ limit: 5 })
  const {
    data: recentCommissions,
    isLoading: commissionsLoading,
    isError: commissionsError,
    refetch: refetchCommissions,
  } = commissionsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports only on TERMINAL failure so TanStack retries don't multiply events.
  useReportQueryError(statsQuery, 'getMyStats failed: ')
  useReportQueryError(analyticsQuery, 'getMyAnalytics failed: ')
  useReportQueryError(commissionsQuery, 'getMyCommissions failed: ')

  const metricsError = statsError || analyticsError

  // Analytics API returns rows newest-first; charts and sparklines read
  // chronologically (oldest on the left), so reverse a copy here.
  const analyticsRows = [...(analytics?.rows ?? [])].reverse()
  const ordersSeries = analyticsRows.map((row) => row.orders)
  const revenueSeries = analyticsRows.map((row) =>
    toAmount(row.revenue_usd, 'dashboard analytics revenue_usd:'),
  )
  const commissionsSeries = analyticsRows.map((row) =>
    toAmount(row.commission_usd, 'dashboard analytics commission_usd:'),
  )

  const totalRevenue = toAmount(stats?.total_revenue_usd, 'dashboard total_revenue_usd:')
  const totalCommissions = toAmount(stats?.total_commission_usd, 'dashboard total_commission_usd:')

  const chartData = analyticsRows.map((row) => ({
    label: row.period_label,
    revenue: toAmount(row.revenue_usd, 'dashboard analytics revenue_usd:'),
    commissions: toAmount(row.commission_usd, 'dashboard analytics commission_usd:'),
  }))

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="dashboard.title" />}
        subtitle={
          partner?.tier && (
            <>
              <FormattedMessage id="dashboard.commissionRate" />:{' '}
              <Typography
                className="font-semibold"
                color="primary"
                component="span"
                variant="body2"
              >
                <FormattedNumber
                  style="percent"
                  value={Number(partner.tier.commission_rate) / 100}
                />
              </Typography>
              {' · '}
              {partner.tier.name}
            </>
          )
        }
      />

      <Page.Content>
        {metricsError ? (
          <InlineQueryError
            data-tst="dashboard-metrics-error"
            messageId="dashboard.error"
            onRetry={() => {
              if (statsError) {
                void refetchStats()
              }
              if (analyticsError) {
                void refetchAnalytics()
              }
            }}
          />
        ) : (
          <>
            <div
              className="flex flex-row flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<ShoppingCartIcon />}
                label={<FormattedMessage id="dashboard.totalOrders" />}
                loading={statsLoading}
                sparklineData={ordersSeries}
                tooltip={formatMessage({ id: 'tooltip.totalOrders' })}
                value={<FormattedNumber value={stats?.total_orders ?? 0} />}
                deltaPct={toNullablePct(
                  stats?.orders_delta_pct,
                  'dashboard toNullablePct orders_delta_pct:',
                )}
              />
              <DashboardKpiTile
                icon={<UserGroupIcon />}
                label={<FormattedMessage id="dashboard.uniqueCustomers" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.uniqueCustomers' })}
                value={<FormattedNumber value={stats?.unique_customers ?? 0} />}
              />
              <DashboardKpiTile
                icon={<CurrencyDollarIcon />}
                label={<FormattedMessage id="dashboard.totalRevenue" />}
                loading={statsLoading}
                sparklineData={revenueSeries}
                tooltip={formatMessage({ id: 'tooltip.totalRevenue' })}
                deltaPct={toNullablePct(
                  stats?.revenue_delta_pct,
                  'dashboard toNullablePct revenue_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalRevenue}
                  />
                }
              />
              <DashboardKpiTile
                icon={<ChartBarIcon />}
                label={<FormattedMessage id="dashboard.totalCommissions" />}
                loading={statsLoading}
                sparklineData={commissionsSeries}
                tooltip={formatMessage({ id: 'tooltip.myCommissions' })}
                deltaPct={toNullablePct(
                  stats?.commission_delta_pct,
                  'dashboard toNullablePct commission_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalCommissions}
                  />
                }
              />
            </div>

            <PerformanceChart
              data={chartData}
              icon={<PresentationChartLineIcon />}
              title={<FormattedMessage id="dashboard.performance" />}
              controls={
                <ToggleButtonGroup
                  size="small"
                  value={granularity}
                  onValueChange={(v) => {
                    if (isGranularity(v)) {
                      setGranularity(v)
                    }
                  }}
                >
                  <ToggleButton value="daily">
                    {formatMessage({ id: 'dashboard.granularity.daily' })}
                  </ToggleButton>
                  <ToggleButton value="weekly">
                    {formatMessage({ id: 'dashboard.granularity.weekly' })}
                  </ToggleButton>
                  <ToggleButton value="monthly">
                    {formatMessage({ id: 'dashboard.granularity.monthly' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              }
              totals={[
                {
                  id: 'revenue',
                  label: <FormattedMessage id="admin.dashboard.revenueThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalRevenue}
                    />
                  ),
                },
                {
                  id: 'commissions',
                  label: <FormattedMessage id="admin.dashboard.commissionsThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalCommissions}
                    />
                  ),
                },
              ]}
            />
          </>
        )}

        <StatsCard
          icon={<ClockIcon />}
          title={<FormattedMessage id="dashboard.recentCommissions" />}
          controls={
            <Button
              as={Link}
              size="small"
              to="/commissions"
              variant="text"
            >
              <FormattedMessage id="dashboard.viewAll" />
            </Button>
          }
        >
          {commissionsError ? (
            <InlineQueryError
              data-tst="dashboard-commissions-error"
              messageId="dashboard.recentOrders.error"
              onRetry={() => void refetchCommissions()}
            />
          ) : (
            <CommissionsTable
              data={recentCommissions?.items ?? []}
              getRowId={(row) => row.id}
              loading={commissionsLoading}
            />
          )}
        </StatsCard>
      </Page.Content>
    </Page>
  )
}
